import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';

import ChatIconContainer from 'presentation/containers/ChatIconContainer';
import CSSTransitionOnMount from 'presentation/components/lib/CSSTransitionOnMount';
import { INITIAL_BUTTON_TYPES } from 'enums/configuration';
import overrideStyle from 'lib/overrideStyle';

import styles from 'presentation/components/styles/MinimizedChatButton.css';
let cx = classnames.bind(styles);

export default function MinimizedChatButton(props) {
  const keyPressHandler = useCallback(
    evt => {
      // Handle enter and space bar key events
      if (evt.keyCode == 13 || evt.keyCode == 32) {
        evt.preventDefault();
        props.onKeyDown(props.shouldUpdateUnreadCount);
      }
    },
    [props.onKeyDown, props.shouldUpdateUnreadCount]
  );

  const onClickHandler = useCallback(
    () => {
      props.onClick(props.shouldUpdateUnreadCount);
      props.alternateBucket ? props.initializeSelfServiceChat() : null;
    },
    [props.onClick, props.shouldUpdateUnreadCount, props.initializeSelfServiceChat]
  );

  let button = renderIcon(props);
  // if there is an existing chat user, never show the text button
  // an existing user is created after onboarding or upon first
  // message when onboarding is disabled
  if (!props.isExistingUser && props.type === INITIAL_BUTTON_TYPES.TEXT) {
    button = renderTextButton(props);
  }

  return (
    <CSSTransitionOnMount className="fadeChatButtonUp" timeout={500}>
      <MinimizedChatButtonStyled
        tabIndex="0"
        role="button"
        className={styles.chatButton_wrapper}
        id="gladlyChatDragHandle"
        onKeyDown={keyPressHandler}
        onClick={onClickHandler}
      >
        {button}
      </MinimizedChatButtonStyled>
    </CSSTransitionOnMount>
  );
}

const MinimizedChatButtonStyled = overrideStyle('div')`
  opacity: 0;
  transform: translateY(40px);

  &.fadeChatButtonUp-enter-active {
    opacity: 1;
    transition: opacity 500ms cubic-bezier(0.42, 0, 1, 1), transform 500ms ease;
    transform: translateY(0);
  }
  &.fadeChatButtonUp-enter-done {
    opacity: 1;
    transform: translateY(0);
  }
`;

MinimizedChatButton.propTypes = {
  alternateBucket: PropTypes.bool,
  isExistingUser: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  shouldUpdateUnreadCount: PropTypes.bool,
  type: PropTypes.string,
  unreadMessageCount: PropTypes.number.isRequired,
  value: PropTypes.string,
};

function renderTextButton(props) {
  let minimizedChatButtonClasses = cx('minimizedChatButtonSelector', 'initialChatButton');
  return (
    <ButtonWrapper className={minimizedChatButtonClasses} data-aid="gladlyChatChatButton" tabIndex="-1">
      {props.value}
    </ButtonWrapper>
  );
}

function renderIcon(props) {
  let minimizedIconClasses = cx('minimizedChatButtonSelector', 'minimizedChatButton');
  let notificationClasses = cx(styles.minimizedChatButton_notification, 'minimizedChatButton_notification');

  return (
    <ButtonWrapper className={minimizedIconClasses} data-aid="gladlyChatChatButton" tabIndex="-1">
      {props.unreadMessageCount > 0 && <div className={notificationClasses} />}
      <ChatIconContainer />
    </ButtonWrapper>
  );
}

const ButtonWrapper = overrideStyle('button')`
  font-family: ${p => p.theme.fontLibrary.primary};
`;
